
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, TrendingUp, Bot, ArrowRight } from 'lucide-react';
import Button from '@/components/ui/Button';

const HeroSection = () => {
    const [sliderPosition, setSliderPosition] = useState(50);
    const containerRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        isDragging.current = true;
        document.body.style.userSelect = 'none'; // Prevent text selection during drag
    };

    const handleMouseUp = () => {
        isDragging.current = false;
        document.body.style.userSelect = ''; // Reset text selection
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging.current || !containerRef.current) return;

        updateSliderPosition(e.clientX);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        isDragging.current = true;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging.current || !containerRef.current || e.touches.length === 0) return;

        updateSliderPosition(e.touches[0].clientX);
    };

    const updateSliderPosition = (clientX: number) => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
        const containerWidth = rect.width;

        // Calculate percentage (constrained between 0-100)
        const newPosition = Math.max(0, Math.min(100, (x / containerWidth) * 100));
        setSliderPosition(newPosition);
    };

    useEffect(() => {
        const handleGlobalMouseUp = () => {
            if (isDragging.current) {
                isDragging.current = false;
                document.body.style.userSelect = ''; // Reset text selection
            }
        };

        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (isDragging.current && containerRef.current) {
                updateSliderPosition(e.clientX);
            }
        };

        const handleGlobalTouchMove = (e: TouchEvent) => {
            if (isDragging.current && containerRef.current && e.touches.length > 0) {
                updateSliderPosition(e.touches[0].clientX);
                e.preventDefault(); // Prevent scrolling while dragging
            }
        };

        const handleGlobalTouchEnd = () => {
            if (isDragging.current) {
                isDragging.current = false;
            }
        };

        // Add global event listeners to handle events outside the component
        document.addEventListener('mouseup', handleGlobalMouseUp);
        document.addEventListener('mousemove', handleGlobalMouseMove);
        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleGlobalTouchEnd);

        return () => {
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('touchmove', handleGlobalTouchMove);
            document.removeEventListener('touchend', handleGlobalTouchEnd);
            document.body.style.userSelect = ''; // Reset text selection on cleanup
        };
    }, []);

    return (
        <section className="relative pt-20 lg:pt-24 pb-32 lg:pb-36 overflow-hidden md:h-screen">
            {/* Interactive split background */}
            <div
                ref={containerRef}
                className="absolute inset-0 overflow-hidden"
            >
                {/* Left side - Manual processes (updated with better image) */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-slate-800 opacity-90"></div>
                    <div
                        className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070')] bg-cover bg-center opacity-60 mix-blend-overlay"
                        style={{ backgroundPosition: 'left center' }}
                    ></div>
                    
                </div>

                {/* Right side - Automation (updated with better image) */}
                <div
                    className="absolute inset-0 bg-gradient-to-br from-orcsys-600/30 to-slate-900 z-0"
                    style={{ clipPath: `polygon(${sliderPosition}% 0, 100% 0, 100% 100%, ${sliderPosition}% 100%)` }}
                >
                    <div
                        className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070')] bg-cover opacity-40 mix-blend-overlay"
                        style={{ backgroundPosition: 'right center' }}
                    ></div>

                </div>

                {/* Divider line with higher z-index to ensure it's in front of everything */}
                <div
                    ref={sliderRef}
                    className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize z-[100] hidden lg:block"
                    style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                >
                    {/* Much larger invisible touch area for better grab ability */}
                    <div className="absolute top-0 h-full w-32 left-1/2 -translate-x-1/2 cursor-ew-resize z-[100]"></div>

                    {/* Visible slider handle with higher z-index */}
                    <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg cursor-ew-resize z-[100]">
                        <div className="flex">
                            <ArrowRight className="w-4 h-4 text-slate-700" />
                            <ArrowRight className="w-4 h-4 text-slate-700 rotate-180" />
                        </div>
                    </div>

                    {/* Additional visual indicators along the slider line for better visibility */}
                    <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-6 h-6 bg-white/80 rounded-full z-[100]"></div>
                    <div className="absolute top-3/4 left-1/2 -translate-x-1/2 w-6 h-6 bg-white/80 rounded-full z-[100]"></div>
                </div>
            </div>

            {/* Content overlay */}
            <div className="container px-4 mx-auto relative z-30">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 relative z-40">
                                Automatisez vos processus,{' '}
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orcsys-400 to-orcsys-500">
                                    réduisez vos coûts
                                </span>
                            </h1>
                            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                                Transformez votre entreprise grâce à l'IA et l'automatisation.
                                Réduisez jusqu'à 70% de vos coûts opérationnels et gagnez en efficacité.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="flex flex-col sm:flex-row items-center justify-center gap-4"
                        >
                            <Button.Link
                                href="#calculator"
                                className="bg-orcsys-500 text-white hover:bg-orcsys-600 min-w-[200px] group relative z-40"
                                size="lg"
                            >
                                <span className="flex items-center">
                                    Calculer vos économies
                                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </span>
                            </Button.Link>
                            <Button.Link
                                href="#estimation"
                                variant="outline"
                                className="border-slate-300 text-white hover:bg-white/10 relative z-40"
                                size="lg"
                            >
                                Demander un devis gratuit
                            </Button.Link>
                        </motion.div>
                    </div>

                    {/* Key metrics */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto relative z-40"
                    >
                        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center">
                            <div className="w-12 h-12 rounded-lg bg-orcsys-500/20 flex items-center justify-center mx-auto mb-4">
                                <Clock className="w-6 h-6 text-orcsys-400" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">-40%</h3>
                            <p className="text-slate-300">Temps de traitement</p>
                        </div>

                        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center">
                            <div className="w-12 h-12 rounded-lg bg-orcsys-500/20 flex items-center justify-center mx-auto mb-4">
                                <TrendingUp className="w-6 h-6 text-orcsys-400" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">+85%</h3>
                            <p className="text-slate-300">Précision des processus</p>
                        </div>

                        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center">
                            <div className="w-12 h-12 rounded-lg bg-orcsys-500/20 flex items-center justify-center mx-auto mb-4">
                                <Bot className="w-6 h-6 text-orcsys-400" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">24/7</h3>
                            <p className="text-slate-300">Opérations automatisées</p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default HeroSection;
